import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import './index.css';
import Navbar from './components/navbar';
import Ledger from './pages/Ledger';
import About from './pages/About';
import Calculator from './pages/Calculator';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import CarbonFarming from './pages/CarbonFarming';
import Research from './pages/Research';
import FAQs from './pages/FAQs';
import CustomerStories from './pages/CustomerStories';
import Company from './pages/Company';
import Contact from './pages/Contact';
import useFadeIn from './hooks/useFadeIn';
import { AuthProvider } from './AuthContext';
import './firebase';

// Swift-inspired Gradient Blob component
const GradientBlob = ({ className, colors }) => {
  return (
    <div className={`absolute rounded-full filter blur-3xl opacity-60 mix-blend-multiply animate-blob ${className}`}
      style={{ 
        background: `radial-gradient(circle, ${colors[0]} 0%, ${colors[1]} 100%)`,
      }}
    ></div>
  );
};

// Card component for feature highlights
const FeatureCard = ({ title, description, icon, linkTo }) => {
  return (
    <a 
      href={linkTo} 
      className="group relative bg-white/70 backdrop-blur-lg border border-white/20 rounded-2xl p-6 transition-all duration-300 hover:shadow-lg hover:bg-white/90 hover:scale-105 overflow-hidden"
    >
      <div className="absolute -right-6 -top-6 w-16 h-16 rounded-full bg-green-500/10 group-hover:bg-green-500/20 transition-all duration-300"></div>
      <div className="flex items-start space-x-4">
        <div className="bg-green-50 p-3 rounded-xl text-green-600">
          {icon}
        </div>
        <div>
          <h3 className="font-semibold text-gray-800 mb-2">{title}</h3>
          <p className="text-gray-600 text-sm">{description}</p>
        </div>
      </div>
    </a>
  );
};

function AppContent() {
  useFadeIn();

  return (
    <div className="min-h-screen flex flex-col items-center relative overflow-hidden bg-gray-50">
      {/* Background gradient blobs */}
      <GradientBlob 
        className="top-0 -left-10 w-96 h-96 delay-75"
        colors={['rgba(74, 222, 128, 0.4)', 'rgba(16, 185, 129, 0.35)']}
      />
      <GradientBlob 
        className="bottom-1/3 right-0 w-72 h-72 delay-150"
        colors={['rgba(16, 185, 129, 0.3)', 'rgba(5, 150, 105, 0.25)']}
      />
      <GradientBlob 
        className="bottom-0 left-1/4 w-80 h-80"
        colors={['rgba(134, 239, 172, 0.35)', 'rgba(74, 222, 128, 0.3)']}
      />
      
      <header className="w-full z-10">
        <Navbar />
      </header>
      
      <main className="flex-grow flex items-center justify-center text-black fade-in w-full z-10 px-4 md:px-8">
        <Routes>
          <Route path="/ledger" element={<Ledger />} />
          <Route path="/about" element={<About />} />
          <Route path="/calculator" element={<Calculator />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/resources/carbon-farming" element={<CarbonFarming />} />
          <Route path="/resources/research" element={<Research />} />
          <Route path="/resources/faqs" element={<FAQs />} />
          <Route path="/resources/customer-stories" element={<CustomerStories />} />
          <Route path="/company/about" element={<Company />} />
          <Route path="/company/contact" element={<Contact />} />
          <Route path="/" element={
            <div className="w-full max-w-6xl mx-auto">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-12 py-12">
                {/* Hero content */}
                <div className="lg:w-1/2">
                  <h1 className="text-5xl md:text-6xl font-bold text-gray-800 mb-4">
                    <span className="text-emerald-600">
                      Welcome to
                    </span>
                    <br />
                    <span className="font-extrabold italic bg-gradient-to-r from-teal-500 to-green-500 bg-clip-text text-transparent">SphereSix</span>
                  </h1>
                  <p className="text-gray-600 text-lg md:text-xl mb-8">
                    Calculate, track, and reduce your carbon footprint with our innovative tools designed for sustainable farming practices.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <a 
                      href="/calculator" 
                      className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-medium rounded-xl shadow-md hover:shadow-lg transform transition hover:-translate-y-1"
                    >
                      Get Started
                    </a>
                    <a 
                      href="/about" 
                      className="px-6 py-3 bg-white text-green-600 font-medium rounded-xl shadow-sm border border-gray-100 hover:shadow-md transform transition hover:-translate-y-1"
                    >
                      Learn More
                    </a>
                  </div>
                </div>
                
                {/* Hero "image" (gradient blob art) */}
                <div className="lg:w-1/2 h-64 md:h-96 relative bg-white/30 backdrop-blur-md rounded-2xl overflow-hidden shadow-lg border border-white/40">
                  <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-green-400/40 rounded-full filter blur-xl"></div>
                  <div className="absolute top-1/3 right-1/4 w-48 h-48 bg-emerald-500/30 rounded-full filter blur-xl"></div>
                  <div className="absolute bottom-1/4 left-1/3 w-40 h-40 bg-lime-400/30 rounded-full filter blur-xl"></div>
                  <div className="absolute inset-0 backdrop-blur-sm flex items-center justify-center">
                    <div className="text-green-600/90 font-bold italic text-4xl">Grow Green to Earn Green</div>
                  </div>
                </div>
              </div>

              {/* Feature cards section */}
              <div className="my-16">
                <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">Our Features</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <FeatureCard 
                    title="Carbon Ledger" 
                    description="Track your carbon sequestration and offset activities over time with our comprehensive ledger system."
                    icon={
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                      </svg>
                    }
                    linkTo="/ledger"
                  />
                  <FeatureCard 
                    title="Carbon Calculator" 
                    description="Estimate your carbon savings and potential earnings with our specialized carbon calculator for agricultural practices."
                    icon={
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                      </svg>
                    }
                    linkTo="/calculator"
                  />
                  <FeatureCard 
                    title="About Our Mission" 
                    description="Learn about our commitment to sustainable agriculture and how we're helping farmers reduce their carbon footprint."
                    icon={
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                    }
                    linkTo="/about"
                  />
                </div>
              </div>

              {/* Testimonial/stats section */}
              <div className="my-16 bg-white/70 backdrop-blur-lg rounded-2xl p-8 border border-white/20 shadow-lg">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-800 mb-2">Making A Difference</h2>
                  <p className="text-gray-600">Our impact in numbers</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-600 mb-2">15,000+</div>
                    <div className="text-gray-600">Acres Participating</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-600 mb-2">8,500</div>
                    <div className="text-gray-600">Tons of Carbon Sequestered</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-600 mb-2">250+</div>
                    <div className="text-gray-600">Farmers Enrolled</div>
                  </div>
                </div>
              </div>
            </div>
          } />
        </Routes>
      </main>
      
      <footer className="w-full z-10 bg-white/80 backdrop-blur-md border-t border-gray-100 py-6 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between">
          <div className="mb-4 md:mb-0">
            <div className="font-bold text-lg bg-gradient-to-r from-teal-500 to-green-500 bg-clip-text text-transparent">SphereSix</div>
            <div className="text-green-500 text-sm">Sustainable farming solutions</div>
          </div>
          <div className="flex space-x-6">
            <a href="/about" className="text-green-500 hover:text-teal-500 transition-colors">About</a>
            <a href="/calculator" className="text-green-500 hover:text-teal-500 transition-colors">Calculator</a>
            <a href="/company/contact" className="text-green-500 hover:text-teal-500 transition-colors">Help</a>
            <a href="/login" className="text-green-500 hover:text-teal-500 transition-colors">Login</a>
          </div>
          <div className="text-green-500 text-sm mt-4 md:mt-0">
            © SphereSix 2025. All rights reserved.
          </div>
        </div>
      </footer>
      
      {/* Add some CSS animations */}
      <style jsx>{`
        @keyframes blob {
          0% {
            transform: scale(1) translate(0px, 0px);
          }
          33% {
            transform: scale(1.1) translate(20px, -10px);
          }
          66% {
            transform: scale(0.9) translate(-20px, 10px);
          }
          100% {
            transform: scale(1) translate(0px, 0px);
          }
        }
        .animate-blob {
          animation: blob 7s infinite alternate;
        }
        .delay-75 {
          animation-delay: 0s;
        }
        .delay-150 {
          animation-delay: -2s;
        }
        .delay-300 {
          animation-delay: -4s;
        }
      `}</style>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;